import {
  Box,
  Link,
  system,
  SystemProps,
  RoundedButton,
  FlexProps,
} from "flicket-ui";
import Image from "next/future/image";
import { ReactNode, useContext, useState } from "react";
import styled from "styled-components";
import { Icon } from "~components/common/Icon";
import { Sidebar } from "~components/common/Menu/menu.Sidebar";
import { BaseContext } from "~context";
import { getImageSrc } from "~lib/helpers";

import { FixedSizeImage } from "../Image";
import { useOrganization } from "~hooks";
import { IdentificationBadge } from "@phosphor-icons/react";

interface MenuProps extends FlexProps {
  isLoggedIn: boolean;
  pathname: string;
  logout: () => void;
  children: ReactNode;
  forceOnMobile?: boolean;
}

const StyledMenu = styled(Box)<SystemProps<typeof Box>>`
  width: 100%;
  padding: 12px 16px;

  height: 64px;
  max-height: 64px;

  /* background-color: ${(p) => p.theme.colors.P300}; */

  box-shadow: ${(p) => p.theme.shadows.sm};

  font-size: ${(p) => p.theme.fontSizes[3]};
  letter-spacing: -0.165px;

  position: relative;

  ${system}
`;

const MobileBackground = styled(Box)`
  position: absolute;

  max-height: 64px;
  height: 64px;

  top: 0;
  left: 0;

  width: 100%;

  background-color: ${(p) => p.theme.colors.P300};

  z-index: 1;
`;

const visiblePaths = [
  "/",
  "/events",
  "/merchandise",
  "/memberships",
  "/members-area",
  "/not-found",
  "/404",
  "/account",
  "/tickets",
  "/order",
  "/tours/[tourId]",
  "/calendar",
];

export const MobileMenu = ({
  isLoggedIn,
  logout,
  pathname = "/",
  children,
  forceOnMobile = false,
  ...props
}: MenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { organization, t } = useOrganization();
  const { isSinglePage } = useContext(BaseContext);
  const { mobileLogo, background } = organization?.branding || {};

  // this is needed so we have the menu header for a single event set up.
  if (isSinglePage) {
    visiblePaths.push("/events/[eventId]");
  }

  const openMenu = () => setIsOpen(true);
  const closeMenu = () => setIsOpen(false);
  const bgImageUrl = getImageSrc(organization?.branding?.background, "");
  return (
    (visiblePaths.includes(pathname) || forceOnMobile) && (
      <StyledMenu d="flex" flexDirection="column" {...props}>
        <MobileBackground>
          {bgImageUrl && (
            <Image
              src={bgImageUrl}
              fill={true}
              alt={`${organization.name}`}
              priority={true}
              style={{ objectFit: "cover", objectPosition: "center" }}
            />
          )}
        </MobileBackground>

        <Sidebar
          isLoggedIn={isLoggedIn}
          isOpen={isOpen}
          logout={logout}
          onClose={closeMenu}
        />
        <Box
          d="flex"
          alignItems="center"
          justifyContent="space-between"
          zIndex={"2" as any}
        >
          <Box d="flex">
            <Link
              to={
                organization?.slug === "artfair"
                  ? "http://www.artfair.co.nz"
                  : "/"
              }
            >
              <FixedSizeImage
                image={mobileLogo}
                fallback="/static/flicket-white.png"
                alt={`${organization.name} logo`}
                sizes={"(min-width: 200px) 200px"}
                priority={true}
                width={50}
                height={30}
              />
            </Link>
          </Box>

          <Box d="flex" alignItems="center">
            {!isSinglePage && !!organization?.showMemberships && (
              <RoundedButton
                mr={3}
                size="md"
                variant={background ? "primary" : "solid"}
                href={
                  isLoggedIn
                    ? "/members-area"
                    : "/login?redirect=%2Fmembers-area"
                }
              >
                <IdentificationBadge weight="fill" />
                {t("membership_name_plural")} Area
              </RoundedButton>
            )}

            <Icon fontSize={6} icon="menu" color="white" onClick={openMenu} />
          </Box>
        </Box>
        {children}
      </StyledMenu>
    )
  );
};
